import type { FC } from 'react'

import { Info } from 'lucide-react'
import ButtonLink from '@/components/landing/button-link'
import FairnessConsole from '@/components/landing/fairness-console'
import { Accent, Eyebrow } from '@/components/landing/section-heading'
import { LINKS } from '@/constants/kinematic'

const FACTS = [
    { value: '1', label: 'valid proof per bet, from your own VRF key' },
    { value: '0', label: 'keys at Kinematic that can decide an outcome' },
    { value: '6', label: 'steps, every one checkable in the browser' }
]

const FairnessSection: FC = () => {
    return (
        <section id='fairness' aria-labelledby='fairness-title' className='relative overflow-hidden border-t border-white/[0.06] py-24 md:py-32'>
            <div aria-hidden='true' className='pointer-events-none absolute -right-40 top-1/4 h-[520px] w-[620px] rounded-full bg-mint/[0.06] blur-[130px]' />

            <div className='relative mx-auto grid max-w-[1320px] gap-14 px-5 md:px-8 lg:grid-cols-12 lg:gap-10'>
                <div className='lg:sticky lg:top-28 lg:col-span-5 lg:self-start'>
                    <Eyebrow index='04' label='Provably fair' />

                    <h2
                        id='fairness-title'
                        className='mt-6 text-balance text-[clamp(2.5rem,5.6vw,5rem)] font-semibold leading-[0.94] tracking-[-0.032em] text-bone stretch-condensed'
                    >
                        Your players can check <Accent className='text-mint'>every bet</Accent> in the browser.
                    </h2>

                    <p className='mt-6 max-w-[34rem] text-base leading-relaxed text-steel md:text-[17px]'>
                        Neither you nor Kinematic can pick a different outcome for a bet, because any other result fails the check against your server&apos;s published key.
                    </p>

                    <ul className='mt-10 grid grid-cols-3 gap-px overflow-hidden rounded-2xl border border-white/[0.07] bg-white/[0.07]'>
                        {FACTS.map(fact => (
                            <li key={fact.label} className='bg-ink p-4'>
                                <span className='block text-4xl font-semibold leading-none tracking-[-0.03em] text-bone stretch-condensed'>{fact.value}</span>
                                <span className='mt-2 block text-xs leading-snug text-steel'>{fact.label}</span>
                            </li>
                        ))}
                    </ul>

                    <div className='mt-6 flex gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4'>
                        <Info aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-steel' />
                        <p className='text-sm leading-relaxed text-steel'>
                            Solana records are for the sandbox. Live mode records nothing on chain until the audited program is on mainnet, and browser checks work in both.
                        </p>
                    </div>

                    <div className='mt-8'>
                        <ButtonLink href={LINKS.provablyFair} variant='ghost' size='md' external>
                            Read how the proofs work
                        </ButtonLink>
                    </div>
                </div>

                <div className='lg:col-span-7'>
                    <FairnessConsole />
                </div>
            </div>
        </section>
    )
}

export default FairnessSection
